import { useState, useRef } from 'react'
import type { TenantBootstrapModel } from '@unified-lk/tenant-config'
import { staffApi } from '../../../api/staff'
import type { AuthSession } from '../../../types/plans'

export interface PatientSearchResult {
  id: number
  full_name: string
  phone?: string | null
  phone_hidden?: boolean
}

export function PatientSearchInput({
  bootstrap,
  session,
  selectedPatient,
  onSelect,
}: {
  bootstrap: TenantBootstrapModel
  session: AuthSession
  selectedPatient: PatientSearchResult | null
  onSelect: (patient: PatientSearchResult | null) => void
}) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<PatientSearchResult[]>([])
  const [searching, setSearching] = useState(false)
  const [open, setOpen] = useState(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  function handleChange(value: string) {
    setQuery(value)
    onSelect(null)

    if (debounceRef.current) clearTimeout(debounceRef.current)

    if (value.trim().length < 3) {
      setResults([])
      setOpen(false)
      return
    }

    debounceRef.current = setTimeout(() => {
      setSearching(true)
        staffApi.getPatientsList(bootstrap, session, { search: value.trim(), perPage: 8 })
        .then((data: { patients: Array<{ id: number; full_name: string; phone?: string | null; phone_hidden?: boolean }> }) => {
          setResults(data.patients.map(p => ({ id: p.id, full_name: p.full_name, phone: p.phone ?? null, phone_hidden: p.phone_hidden ?? false })))
          setOpen(true)
        })
        .catch(() => setResults([]))
        .finally(() => setSearching(false))
    }, 400)
  }

  function handleSelect(patient: PatientSearchResult) {
    onSelect(patient)
    setQuery(patient.full_name)
    setOpen(false)
  }

  return (
    <div className="relative">
      <div className="relative">
        <input
          className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 pr-8"
          placeholder="Имя пациента (мин. 3 символа)"
          value={selectedPatient ? `${selectedPatient.full_name} (ID: ${selectedPatient.id})` : query}
          onChange={e => handleChange(e.target.value)}
          onFocus={() => { if (results.length > 0) setOpen(true) }}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          autoComplete="off"
        />
        {searching && (
          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs">...</span>
        )}
      </div>

      {open && results.length > 0 && (
        <ul className="absolute z-50 mt-1 w-full rounded-lg border border-slate-200 bg-white shadow-lg max-h-48 overflow-y-auto">
          {results.map((patient) => (
            <li key={patient.id}>
              <button
                type="button"
                className="w-full px-3 py-2 text-left text-sm hover:bg-blue-50 flex justify-between items-center gap-2"
                onMouseDown={() => handleSelect(patient)}
              >
                <span className="font-medium text-slate-800 truncate">{patient.full_name}</span>
                <span className="text-xs text-slate-400 shrink-0">
                  {patient.phone_hidden ? 'телефон скрыт' : (patient.phone ? patient.phone : `ID: ${patient.id}`)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {open && results.length === 0 && !searching && query.trim().length >= 3 && (
        <div className="absolute z-50 mt-1 w-full rounded-lg border border-slate-200 bg-white shadow px-3 py-2 text-xs text-slate-500">
          Пациенты не найдены
        </div>
      )}
    </div>
  )
}
